import React from 'react';
import {
  Download,
  LogIn,
  LogOut,
  Mail,
  ShieldCheck,
  Trash2,
  User,
  X,
} from 'lucide-react';

function ActionButton({ children, className = '', ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
export function AccountModal({
  open,
  onDismiss,
  account,
  syncLoading = false,
  syncError = '',
  favoriteCount = 0,
  historyCount = 0,
  onExport,
  onDelete,
}) {
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteText, setDeleteText] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setBusy('');
      setMessage('');
      setDeleteOpen(false);
      setDeleteText('');
    }
  }, [open]);

  if (!open) return null;

  const run = async (name, action, successMessage = '') => {
    setBusy(name);
    setMessage('');
    try {
      await action();
      if (successMessage) setMessage(successMessage);
    } catch (error) {
      setMessage(error?.message || 'That did not work. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const signedIn = Boolean(account.user);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div
        className="relative max-h-[min(92vh,760px)] w-full max-w-lg overflow-y-auto rounded-3xl border border-emerald-100 bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-3 rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          aria-label="Close account"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 pr-10">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <User className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">VegAI</p>
            <h2 id="account-title" className="text-xl font-black text-slate-950">
              {signedIn ? 'Your account' : 'Sign in'}
            </h2>
          </div>
        </div>

        {!signedIn ? (
          <div className="mt-6">
            <section className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm leading-6 text-slate-700">
              <p className="font-bold text-slate-950">Accounts are optional.</p>
              <p className="mt-1">
                Without an account, VegAI keeps working exactly as it does today and saves
                Favorites and History only in this browser.
              </p>
            </section>

            {!account.configured ? (
              <p className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
                Account sign-in is not configured in this VegAI environment yet.
              </p>
            ) : (
              <>
                <ActionButton
                  onClick={() => run('google', account.signInWithGoogle)}
                  disabled={Boolean(busy)}
                  className="mt-5 w-full bg-slate-950 text-white hover:bg-slate-800"
                >
                  <LogIn className="h-4 w-4" />
                  {busy === 'google' ? 'Opening Google...' : 'Continue with Google'}
                </ActionButton>

                <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  <span className="h-px flex-1 bg-slate-200" />
                  or
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    run(
                      'email',
                      () => account.sendMagicLink(email),
                      'Check your email for a one-time sign-in link.'
                    );
                  }}
                >
                  <label htmlFor="account-email" className="text-sm font-bold text-slate-800">
                    Email magic link
                  </label>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input
                      id="account-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="min-w-0 flex-1 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                    <ActionButton
                      type="submit"
                      disabled={Boolean(busy)}
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      <Mail className="h-4 w-4" />
                      {busy === 'email' ? 'Sending...' : 'Email link'}
                    </ActionButton>
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    No password. The emailed link signs you in once.
                  </p>
                </form>
              </>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <section className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                <div className="min-w-0">
                  <p className="break-words text-sm font-black text-slate-950">
                    {account.user.email || 'Signed-in user'}
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {syncLoading
                      ? 'Loading your synced data...'
                      : `${favoriteCount} favorites and ${historyCount} history items synced.`}
                  </p>
                </div>
              </div>
            </section>

            {syncError && (
              <p className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">
                {syncError}
              </p>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <ActionButton
                onClick={() => run('export', onExport, 'Your data download is ready.')}
                disabled={Boolean(busy)}
                className="border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
              >
                <Download className="h-4 w-4" />
                {busy === 'export' ? 'Preparing...' : 'Export my data'}
              </ActionButton>
              <ActionButton
                onClick={() => run('signout', account.signOut)}
                disabled={Boolean(busy)}
                className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              >
                <LogOut className="h-4 w-4" />
                {busy === 'signout' ? 'Signing out...' : 'Sign out'}
              </ActionButton>
            </div>

            <section className="rounded-2xl border border-red-200 bg-red-50/50 p-4">
              <p className="text-sm font-black text-red-900">Delete account</p>
              <p className="mt-1 text-sm leading-6 text-red-800">
                This permanently deletes your account, synced Favorites, and synced History.
                Guest data saved separately in this browser is not automatically removed.
              </p>
              {!deleteOpen ? (
                <ActionButton
                  onClick={() => setDeleteOpen(true)}
                  className="mt-3 border border-red-300 bg-white text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete my account
                </ActionButton>
              ) : (
                <div className="mt-3">
                  <label htmlFor="delete-account-confirmation" className="text-sm font-bold text-red-900">
                    Type DELETE to confirm
                  </label>
                  <input
                    id="delete-account-confirmation"
                    value={deleteText}
                    onChange={(event) => setDeleteText(event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-red-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <ActionButton
                      onClick={() => run('delete', onDelete)}
                      disabled={deleteText !== 'DELETE' || Boolean(busy)}
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                      {busy === 'delete' ? 'Deleting...' : 'Permanently delete'}
                    </ActionButton>
                    <ActionButton
                      onClick={() => {
                        setDeleteOpen(false);
                        setDeleteText('');
                      }}
                      disabled={Boolean(busy)}
                      className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </ActionButton>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {message && (
          <p className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-semibold text-slate-700">
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
